/**
 * Shared icon set for the Sage web chrome.
 *
 * The Canopy standard icon library is **Font Awesome 6 Pro, Sharp Light**
 * (design-standards/components/icons.md — verified against the Figma kit,
 * whose icon components are FA Sharp Light glyph wrappers). Canopy's FA Pro
 * npm license is configured in the repo .npmrc (same registry setup as
 * canopy-portal).
 *
 * Pages never import Font Awesome directly — everything goes through this
 * module (inventory.md rule 3: one icon family per app), so a future style
 * decision (see decisions-needed.md "Icon style unification") is a one-file
 * change. Icons render as tree-shaken inline SVG, inherit `currentColor`,
 * and size via the `size` prop (default 14, from the kit's scale steps
 * 12/14/16/20/24/28/32/36/40).
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faChevronRight,
  faChevronDown,
  faChevronLeft,
  faFolder,
  faFolderOpen,
  faFileLines,
  faGrid2,
  faDisplay,
  faUser,
  faEllipsisVertical,
  faMagnifyingGlass,
  faClock,
  faUpRightFromSquare,
  faGaugeHigh,
  faChartColumn,
  faWifi,
  faReceipt,
  faPowerOff,
  faRobot,
} from '@fortawesome/sharp-light-svg-icons';

interface IconProps {
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

function Fa({
  icon,
  size = 14,
  className,
  style,
}: IconProps & { icon: IconDefinition }): JSX.Element {
  return (
    <FontAwesomeIcon
      icon={icon}
      className={className}
      style={{ width: size, height: size, ...style }}
      aria-hidden="true"
    />
  );
}

/** Right-pointing chevron (collapsed/expand affordance). */
export function ChevronRight(p: IconProps = {}): JSX.Element {
  return <Fa {...p} icon={faChevronRight} />;
}

/** Down-pointing chevron (expanded state). */
export function ChevronDown(p: IconProps = {}): JSX.Element {
  return <Fa {...p} icon={faChevronDown} />;
}

/** Left/right list-panel chevron used by the collapse toggle. */
export function PanelChevron({ collapsed, ...p }: IconProps & { collapsed: boolean }): JSX.Element {
  return <Fa {...p} icon={collapsed ? faChevronRight : faChevronLeft} />;
}

export function NavigationPanelIcon({
  collapsed,
  ...p
}: IconProps & { collapsed: boolean }): JSX.Element {
  return <Fa {...p} icon={collapsed ? faChevronRight : faChevronLeft} />;
}

/** Folder; `open` lifts the lid. */
export function FolderIcon({ open = false, ...p }: IconProps & { open?: boolean }): JSX.Element {
  return <Fa {...p} icon={open ? faFolderOpen : faFolder} />;
}

/** CSAGE-564 — "open this" affordance on a library card. */
export function OpenInIcon(p: IconProps = {}): JSX.Element {
  return <Fa {...p} icon={faUpRightFromSquare} />;
}

/** CSAGE-564 — a faint subject watermark for a library card, chosen from the
 * item's name (with a type fallback) so the grid reads with visual variety and
 * hints what each dashboard/report is about. */
function watermarkIconFor(name: string, kind: string): IconDefinition {
  const n = name.toLowerCase();
  if (/availab|uptime|health/u.test(n)) return faGaugeHigh;
  if (/kpi|metric|score|summary|component/u.test(n)) return faChartColumn;
  if (/network|connectiv/u.test(n)) return faWifi;
  if (/transaction|cash|coin|payment|revenue|sales/u.test(n)) return faReceipt;
  if (/offline|down/u.test(n)) return faPowerOff;
  if (/automation|action|workflow|robot/u.test(n)) return faRobot;
  if (/device|hardware|detail/u.test(n)) return faDisplay;
  if (/event|history|log|time/u.test(n)) return faClock;
  return kind === 'Dashboard' ? faGrid2 : faFileLines;
}
export function CardWatermarkIcon({
  name,
  kind,
  ...p
}: IconProps & { name: string; kind: string }): JSX.Element {
  return <Fa {...p} icon={watermarkIconFor(name, kind)} />;
}

/** Report document. */
export function ReportIcon(p: IconProps = {}): JSX.Element {
  return <Fa {...p} icon={faFileLines} />;
}

/** Dashboard (tile grid). */
export function DashboardIcon(p: IconProps = {}): JSX.Element {
  return <Fa {...p} icon={faGrid2} />;
}

/** Kiosk / signage display. */
export function KioskIcon(p: IconProps = {}): JSX.Element {
  return <Fa {...p} icon={faDisplay} />;
}

/** Person (assigned-reports grouping). */
export function UserIcon(p: IconProps = {}): JSX.Element {
  return <Fa {...p} icon={faUser} />;
}

/** Kebab (vertical three-dot row-actions trigger). */
export function KebabIcon(p: IconProps = {}): JSX.Element {
  return <Fa {...p} icon={faEllipsisVertical} />;
}

/** Search magnifier. */
export function SearchIcon(p: IconProps = {}): JSX.Element {
  return <Fa {...p} icon={faMagnifyingGlass} />;
}

/** Clock (schedules / subscriptions affordance). */
export function ClockIcon(p: IconProps = {}): JSX.Element {
  return <Fa {...p} icon={faClock} />;
}
